/**
 * Module dependencies.
 */

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { cn } from 'app/lib/utils';
import React, { ReactNode } from 'react';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isOpen: boolean;
  buttons: ReactNode;
  isLoading: boolean;
  maskClosable?: boolean;
  children: JSX.Element;
  onClose?: () => void;
  title?: string;
};

/**
 * Export `Modal` component.
 */

export function Modal(props: Props): JSX.Element {
  const { className, buttons, maskClosable, children, isLoading, onClose, isOpen, title } = props;
  const hasButtons = Array.isArray(buttons) ? buttons.length > 0 : buttons != null;

  return (
    <Dialog
      onOpenChange={open => {
        if (!open && !isLoading) {
          onClose?.();
        }
      }}
      open={isOpen}
    >
      <DialogContent
        className={cn(isLoading && '[&>button]:hidden', className)}
        onInteractOutside={event => {
          if (maskClosable === false || isLoading) {
            event.preventDefault();
          }
        }}
      >
        {/* Every dialog needs a title for accessibility (Radix warns otherwise).
            Render it visibly when provided; otherwise keep it screen-reader-only. */}
        <DialogHeader className={cn(!title && 'sr-only')}>
          <DialogTitle>{title ?? 'Dialog'}</DialogTitle>
        </DialogHeader>

        {children}

        {hasButtons && <DialogFooter>{buttons}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
